import { getFormatter, ListedInstrument, parseDateFromEStreamChart } from '@eih-netbroker/common';
import { useCallback } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { uuid } from '../../../../shared/utils/uuidUtils';
import { ProductDetailsSectionItem } from '../../components/product/ProductDetailsSection';
import { ProductDetailsBase, ProductDetailsBaseMaybe } from '../../states/productDetailsStateAtom';
import { ProductDetailsBaseVisibleFields } from './useProductDetailsListsForShare';

const dateFieldsName: Array<keyof ProductDetailsBase> = ['annualHighDate', 'annualLowDate', 'lastPriceTime'];
const unknownValue = '';
const productDetailsFieldsWithCurrencyParams: Array<keyof ProductDetailsBase> = [
  'dailyChange',
  'volumeValue',
  'dividend',
];

export const useProductDetailsCommon = (instrument: ListedInstrument | null) => {
  const { translation, translateWithParams } = useLocale();
  const { formatDateAndTimeShort } = getFormatter();

  const getProductDetailItemLabelAndValue = useCallback(
    (product: ProductDetailsBase, f: keyof ProductDetailsBaseVisibleFields): ProductDetailsSectionItem => {
      if (!instrument) {
        throw new Error('invalid instrument');
      }

      const label = productDetailsFieldsWithCurrencyParams.includes(f)
        ? translateWithParams(translation.productDetails.productDetailsFields[f], {
            currency: instrument.currency,
          })
        : translation.productDetails.productDetailsFields[f];

      const res: ProductDetailsSectionItem = {
        id: label,
        label,
        value: '',
      };

      if (dateFieldsName.includes(f)) {
        try {
          res.value = formatDateAndTimeShort(parseDateFromEStreamChart(product[f]));
        } catch {
          res.value = product[f];
        }
      } else {
        res.value = product[f];
      }

      return res;
    },
    [formatDateAndTimeShort, instrument, translateWithParams, translation.productDetails.productDetailsFields],
  );

  const getTradingOrFundamentalSection = useCallback(
    (
      cols: number,
      product: ProductDetailsBaseMaybe,
      fields: Array<keyof ProductDetailsBaseVisibleFields>,
    ): Array<Array<ProductDetailsSectionItem>> => {
      if (!product.isLoaded) {
        return [];
      }

      const res: Array<Array<ProductDetailsSectionItem>> = new Array<Array<ProductDetailsSectionItem>>();
      res.push([]);
      let colIndex = 0;
      const maxItemInColumn = Math.ceil(fields.length / cols);

      // create columns with maxItemInColumn items in each
      for (let i = 0; i < fields.length; i++) {
        res[colIndex].push(getProductDetailItemLabelAndValue(product, fields[i]));

        if (i > 0 && res[colIndex].length === maxItemInColumn && maxItemInColumn !== fields.length) {
          res.push([]);
          colIndex++;
        }
      }

      // fill up empty spaces in the last column
      for (let i = 0; i <= maxItemInColumn; i++) {
        if (i > res[colIndex].length) {
          res[colIndex].push({ id: uuid(), label: '', value: '' });
        }
      }

      return res;
    },
    [getProductDetailItemLabelAndValue],
  );

  const getInstrumentSection = useCallback(
    (cols: number, product: ProductDetailsBaseMaybe, fields: Array<ProductDetailsSectionItem>) => {
      if (!product.isLoaded) {
        return [];
      }

      const res: Array<Array<ProductDetailsSectionItem>> = new Array<Array<ProductDetailsSectionItem>>();
      res.push([]);
      let colIndex = 0;
      const maxItemInColumn = Math.ceil(fields.length / cols);

      // create columns with maxItemInColumn items in each
      for (let i = 0; i < fields.length; i++) {
        res[colIndex].push(fields[i]);

        if (i > 0 && res[colIndex].length === maxItemInColumn && maxItemInColumn !== fields.length) {
          res.push([]);
          colIndex++;
        }
      }

      // fill up empty spaces in the last column
      for (let i = 0; i <= maxItemInColumn; i++) {
        if (i > res[colIndex].length) {
          res[colIndex].push({ id: uuid(), label: '', value: '' });
        }
      }
      return res;
    },
    [],
  );

  return {
    getProductDetailItemLabelAndValue,
    unknownValue,
    getTradingOrFundamentalSection,
    getInstrumentSection,
  };
};
